"use client";

import { useState } from "react";
import Link from "next/link";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Scale, Mail, ArrowLeft } from "lucide-react";
import { GlassCard } from "@/components/design-system/GlassCard";
import { CyberButton } from "@/components/design-system/CyberButton";
import { HolographicText } from "@/components/design-system/HolographicText";
import { useForgotPassword } from "@/hooks/use-forgot-password";
import { useNotificationStore } from "@/stores/notifications";

const forgotPasswordSchema = z.object({
  email: z.string().email("Enter a valid email address"),
});

type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;

export default function ForgotPasswordPage() {
  const addNotification = useNotificationStore((s) => s.addNotification);
  const [submitted, setSubmitted] = useState(false);
  const forgotPassword = useForgotPassword();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
  });

  const onSubmit = async (data: ForgotPasswordFormData) => {
    try {
      await forgotPassword.mutateAsync(data);
    } catch {
      // Swallow errors — always show the same message for security
    }
    setSubmitted(true);
    addNotification({ type: "system", title: "Reset Link Sent", message: "If an account exists, a reset link has been sent to your email." });
  };

  return (
    <div className="space-y-6">
      {/* Logo & Title */}
      <div className="text-center space-y-3">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-neon-cyan/10 border border-neon-cyan/30 mb-2">
          <Scale className="w-8 h-8 text-neon-cyan" />
        </div>
        <HolographicText as="h1" className="text-3xl block">
          FrameCounsel
        </HolographicText>
        <p className="text-white/50 text-sm">
          Account Recovery
        </p>
      </div>

      {/* Forgot Password Card */}
      <GlassCard variant="strong" padding="lg" neonBorder>
        {submitted ? (
          <div className="space-y-5">
            <div className="text-center mb-6">
              <h2 className="text-lg font-[family-name:var(--font-space-grotesk)] font-semibold text-white/90">
                Check Your Email
              </h2>
            </div>

            <div className="p-3 rounded-lg bg-neon-green/10 border border-neon-green/30 text-neon-green text-sm">
              If an account exists, a reset link has been sent.
            </div>

            <p className="text-xs text-white/40 text-center">
              Check your inbox and spam folder. The link will expire in 1 hour.
            </p>

            <Link href="/login" className="block">
              <CyberButton
                type="button"
                color="cyan"
                variant="outline"
                fullWidth
                icon={<ArrowLeft className="w-4 h-4" />}
              >
                Back to Sign In
              </CyberButton>
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            <div className="text-center mb-6">
              <h2 className="text-lg font-[family-name:var(--font-space-grotesk)] font-semibold text-white/90">
                Forgot Password
              </h2>
              <p className="text-xs text-white/40 mt-1">
                Enter your email to receive a password reset link
              </p>
            </div>

            {/* Email */}
            <div className="space-y-1.5">
              <label
                htmlFor="email"
                className="block text-xs font-mono uppercase tracking-wider text-white/50"
              >
                Email Address
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                placeholder="counsel@lawfirm.com"
                className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder:text-white/20 focus:outline-none focus:border-neon-cyan/50 focus:bg-white/[0.07] transition-all duration-200 text-sm"
                {...register("email")}
              />
              {errors.email && (
                <p className="text-neon-red text-xs mt-1">{errors.email.message}</p>
              )}
            </div>

            {/* Submit */}
            <CyberButton
              type="submit"
              color="cyan"
              fullWidth
              loading={forgotPassword.isPending}
              icon={<Mail className="w-4 h-4" />}
            >
              Send Reset Link
            </CyberButton>

            {/* Links */}
            <div className="flex items-center justify-between text-xs">
              <Link
                href="/login"
                className="text-neon-cyan/70 hover:text-neon-cyan transition-colors"
              >
                Back to Sign In
              </Link>
              <Link
                href="/register"
                className="text-white/30 hover:text-white/60 transition-colors"
              >
                Create Account
              </Link>
            </div>
          </form>
        )}
      </GlassCard>

      {/* Footer */}
      <p className="text-center text-[11px] text-white/20">
        Protected by attorney-client privilege encryption.
        <br />
        All evidence handled in compliance with FRE standards.
      </p>
    </div>
  );
}
